import React, { useMemo, useState } from "react";
import ReusableTable from "../components/Table/ReusableTable";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import Button from "../components/Button/Button";
import { ArrowBigLeft, Search } from "lucide-react";

const InvoiceApproval = () => {
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const handleCheckboxClick = (invoice) => {
    setSelectedInvoice(invoice);
  };

  const handleBack = () => {
    setSelectedInvoice(null);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Action",
        accessor: "action",
        size: 80,
        Cell: ({ row }) => (
          <input
            type="checkbox"
            onChange={() => handleCheckboxClick(row.original)}
          />
        ),
      },
      { Header: "Distributor Name", accessor: "distributorName", size: 200 },
      { Header: "APRN Code", accessor: "arnCode", size: 120 },
      { Header: "Invoice Number", accessor: "invoiceNumber", size: 150 },
      { Header: "Invoice Month", accessor: "invoiceMonth", size: 140 },
      { Header: "Amount", accessor: "amount", size: 100 },
      { Header: "GST", accessor: "gst", size: 80 },
      { Header: "Total Amount", accessor: "totalAmount", size: 120 },
      { Header: "Status", accessor: "status", size: 100 },
      { Header: "UTR No", accessor: "utrNo", size: 150 },
      { Header: "Remark", accessor: "remark", size: 200 },
      { Header: "Rejection Remark", accessor: "rejectionRemark", size: 200 },
      {
        Header: "Invoice",
        accessor: "invoiceFile",
        size: 120,
        Cell: ({ value }) => (
          <a
            href={value}
            download
            className="text-blue-600 underline hover:text-blue-800"
          >
            Download
          </a>
        ),
      },
      {
        Header: "Calculation File",
        accessor: "calculationFile",
        size: 150,
        Cell: ({ value }) => (
          <a
            href={value}
            download
            className="text-blue-600 underline hover:text-blue-800"
          >
            Download
          </a>
        ),
      },
    ],
    []
  );

  const fullData = useMemo(
    () => [
      {
        distributorName: "ABC Distributors",
        arnCode: "ARN123456",
        invoiceNumber: "INV0001",
        invoiceMonth: "July 2025",
        amount: "50000",
        gst: "9000",
        totalAmount: "59000",
        status: "Pending",
        utrNo: "",
        remark: "Waiting for UTR",
        rejectionRemark: "",
        invoiceFile: "/downloads/invoice1.pdf",
        calculationFile: "/downloads/calculation1.xlsx",
      },
      {
        distributorName: "XYZ Traders",
        arnCode: "ARN654321",
        invoiceNumber: "INV0002",
        invoiceMonth: "July 2025",
        amount: "30000",
        gst: "5400",
        totalAmount: "35400",
        status: "Approved",
        utrNo: "UTR12345",
        remark: "Paid",
        rejectionRemark: "",
        invoiceFile: "/downloads/invoice2.pdf",
        calculationFile: "/downloads/calculation2.xlsx",
      },
      {
        distributorName: "PQR Investments",
        arnCode: "ARN789123",
        invoiceNumber: "INV0003",
        invoiceMonth: "July 2025",
        amount: "20000",
        gst: "3600",
        totalAmount: "23600",
        status: "Rejected",
        utrNo: "",
        remark: "",
        rejectionRemark: "Missing documents",
        invoiceFile: "/downloads/invoice3.pdf",
        calculationFile: "/downloads/calculation3.xlsx",
      },
    ],
    []
  );

  const filteredData = useMemo(() => {
    return fullData.filter((item) => {
      const matchesSearch =
        item.distributorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.arnCode.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter
        ? item.status.toLowerCase() === statusFilter.toLowerCase()
        : true;

      return matchesSearch && matchesStatus;
    });
  }, [searchTerm, statusFilter, fullData]);

  return (
    <div className="max-w-screen-xl mx-auto p-4 space-y-4">
      {selectedInvoice ? (
        <>
          {/* Back Button */}
          <Button variant="outline" onClick={handleBack}>
            <div className="flex items-center gap-2">
              <ArrowBigLeft className="w-5 h-5" />
              Back to List
            </div>
          </Button>

          {/* Detail Form */}
          <div className="bg-white p-6 rounded shadow space-y-4 mt-4">
            <h2 className="text-xl font-semibold underline">Invoice Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Distributor Name
                </label>
                <InputText
                  value={selectedInvoice.distributorName}
                  readOnly
                  className="w-full px-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  APRN Code
                </label>
                <InputText
                  value={selectedInvoice.arnCode}
                  readOnly
                  className="w-full px-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Invoice Number
                </label>
                <InputText
                  value={selectedInvoice.invoiceNumber}
                  readOnly
                  className="w-full px-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Invoice Month
                </label>
                <InputText
                  value={selectedInvoice.invoiceMonth}
                  readOnly
                  className="w-full px-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Amount</label>
                <InputText
                  value={selectedInvoice.amount}
                  readOnly
                  className="w-full px-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">GST</label>
                <InputText
                  value={selectedInvoice.gst}
                  readOnly
                  className="w-full px-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Total Amount
                </label>
                <InputText
                  value={selectedInvoice.totalAmount}
                  readOnly
                  className="w-full px-2"
                />
              </div>

              <div className="md:col-span-3">
                <label className="block text-sm font-medium mb-1">Remark</label>
                <InputTextarea
                  value={selectedInvoice.remark}
                  readOnly
                  rows={3}
                  className="w-full px-2"
                />
              </div>
            </div>

            <div className="flex justify-end items-center gap-4">
              <Button variant="default" onClick={() => alert("Approved")}>
                Approve
              </Button>
              <Button variant="outline" onClick={() => alert("Rejected")}>
                Reject
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="bg-white p-4 rounded shadow mt-4 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h2 className="text-xl font-semibold underline">
                Invoice Approval
              </h2>

              {/* Filter Section */}
              <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
                {/* Global Search */}
                <div className="relative w-full md:w-64">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    <Search className="w-4 h-4 text-gray-400" />
                  </span>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by Distributor or APRN"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Status Dropdown */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
            </div>

            {/* Table */}
            <ReusableTable columns={columns} data={filteredData} />
          </div>
        </>
      )}
    </div>
  );
};

export default InvoiceApproval;
